/* eslint-disable react/no-array-index-key */
import {
  Block,
  Box,
  Button as DeckHandButton,
  Card,
  CheckboxInput,
  MessageBlock,
  NavList,
  Paragraph,
} from '@deckee/deck-hand';
import classNames from 'classnames/bind';
import PropTypes from 'prop-types';
import React from 'react';
import { Helmet } from 'react-helmet';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import styled, { useTheme } from 'styled-components';
import Pattern from 'url-knife';
import { captureEvent } from '../../../../services/event-capture';
import { openWithInAppBrowser } from '../../../../services/in-app-browser';
import { ROUTE_REGULATIONS } from '../../../../services/route/route-names';
import { transitionToNextRoute } from '../../../../services/route/route-navigation';
import { selectUnitsConfig } from '../../../../services/store/modules/app-controls';
import { formatLatLon } from '../../../helpers/coordinate-helpers';
import BasicDrawerLayout from '../../../layouts/BasicDrawerLayout/BasicDrawerLayout';
import { LayoutBody } from '../../../layouts/components/LayoutBody/LayoutBody';
import IconWithTextLockup from '../../../lockups/IconWithTextLockup/IconWithTextLockup';
import BlueCircleWrapper from '../../../shared/BlueCircleWrapper/BlueCircleWrapper';
import CommentAndShare from '../../../shared/CommentAndShare/CommentAndShare';
import {
  calendar,
  circleWrapper,
  calendarTick,
  externalLink,
  indicatorDiscWrapper,
  location,
  messages,
  pencil,
  ReactSvg,
} from '../../../shared/vector-images';
import CustomDescription from '../../../shared/_deprecated/components/CustomDescription/CustomDescription';
import Button from '../../../shared/_deprecated/elements/Button/Button';
import DisplayImage from '../../../shared/_deprecated/elements/display-image';
import Text from '../../../shared/_deprecated/elements/Text/Text';
import RegulationLocationCard from '../../Regulations/components/RegulationLocationCard';
import styles from './MarkerContainer.scss';

const cx = classNames.bind(styles);

const StyledPartnerIcon = styled.img`
  max-height: 50px;
  max-width: 40px;
`;

const InformationButtonListItem = ({ label, url }) => {
  const handleClickInformationButton = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    captureEvent({
      type: 'marker details: click information button',
      payload: {
        label,
        url,
      },
    });

    openWithInAppBrowser({ url });
  };

  const theme = useTheme();
  return (
    <NavList.Item href={url} chevron onClick={handleClickInformationButton}>
      <IconWithTextLockup
        image={
          <BlueCircleWrapper>
            <ReactSvg
              toDisplay={externalLink()}
              options={{
                fillColor: theme.colors.secondary,
              }}
              width={24}
              height={24}
            />
          </BlueCircleWrapper>
        }
        heading={label}
      />
    </NavList.Item>
  );
};

const MarkerContainerView = (props) => {
  const {
    className,
    headerImage,
    editRoute,
    flaggedForReview,
    allowEditing,
    formattedLatLon,
    markerIcon,
    markerIconDiscColour,
    checkboxes,
    title,
    descriptionHeaderText,
    description,
    customDescription,
    banner,
    image,
    informationButtons,
    addedOrEditedByLabel,
    id,
    markerType,
    sharePath,
    shareText,
    allowComments,
    readCommentsAction,
    addCommentsAction,
    numberOfComments,
    metaTagURL,
    eventDetails,
    buttonHref,
    buttonLabel,
  } = props;

  const handleCustomButtonHref = async (url, e) => {
    try {
      e.preventDefault();
      e.stopPropagation();
      captureEvent({
        type: 'marker details: click custom button href',
        payload: {
          id,
          markerType,
          url,
        },
      });
      await openWithInAppBrowser({ url });
    } catch (err) {
      // Something has gone wrong, just navigate as usual
    }
  };

  const urls = description
    ? Pattern.TextArea.extractAllUrls(description, {
        ip_v4: true,
        ip_v6: false,
        localhost: false,
        intranet: false,
      })
    : [];

  const viewClasses = cx('marker-container', {
    [className]: className,
  });

  let iconComposer = markerIcon?.baseShape()
    ? circleWrapper(markerIcon?.baseShape())
    : null;
  if (iconComposer && markerIconDiscColour) {
    iconComposer = indicatorDiscWrapper(iconComposer);
  }

  const history = useHistory();
  const unitsConfig = useSelector(selectUnitsConfig);
  const theme = useTheme();

  const navToMarkerSummary = () => {
    captureEvent({
      type: 'marker details: view summary',
      payload: {},
    });
    history.push(`/maps/marker/summary/${markerType}/${id}`);
  };

  const latLonDisplay = formattedLatLon
    ? formatLatLon(
        unitsConfig?.coordinate,
        formattedLatLon?.lat,
        formattedLatLon?.lon,
      )
    : null;

  const handleLinkClick = async (url, e) => {
    try {
      e.preventDefault();
      e.stopPropagation();
      await openWithInAppBrowser({ url });
    } catch (err) {
      // Something has gone wrong, just navigate as usual
    }
  };
  return (
    <div className={viewClasses}>
      <Helmet>
        <title>{`Deckee | ${title}`}</title>
        <meta property="og:description" content={description} />
        <meta property="og:url" content={metaTagURL} />

        <meta property="twitter:title" content={`Deckee | ${title}`} />
        <meta property="twitter:description" content={description} />
      </Helmet>
      <LayoutBody>
        {flaggedForReview && (
          <Card>
            <MessageBlock
              className={cx('marker-container__flagged-message')}
              type="warning"
            >
              This marker has been reported for deletion by a community member
              and is currently under review.
            </MessageBlock>
          </Card>
        )}
        {/* Marker header */}

        {(title || headerImage) && (
          <Card>
            {title && (
              <Card.Heading level="1" size="large">
                {title}
              </Card.Heading>
            )}
            {headerImage && (
              <Card.Heading
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <BasicDrawerLayout.HeaderImage alt={title} src={headerImage} />
              </Card.Heading>
            )}

            <Card.Body>
              <Box fontSize="1" mb="3">
                {!eventDetails && <Box mb="2">{latLonDisplay}</Box>}
                {banner?.source && <Box mt="2">Source: {banner.source}</Box>}
              </Box>
              {eventDetails && (
                <Box mb="3">
                  <NavList edge grouped>
                    <NavList.Item>
                      <IconWithTextLockup
                        emphasis="heading"
                        image={
                          eventDetails?.organizerIcon ? (
                            <StyledPartnerIcon
                              src={eventDetails.organizerIcon}
                              alt=""
                            />
                          ) : (
                            <BlueCircleWrapper>
                              <ReactSvg
                                toDisplay={calendarTick()}
                                options={{
                                  fillColor: '#001A70',
                                }}
                                width={24}
                                height={24}
                              />
                            </BlueCircleWrapper>
                          )
                        }
                        heading={eventDetails.organizer}
                        description="Event supporter"
                      />
                    </NavList.Item>
                    <NavList.Item>
                      <IconWithTextLockup
                        emphasis="heading"
                        image={
                          <BlueCircleWrapper>
                            <ReactSvg
                              toDisplay={calendar()}
                              options={{
                                fillColor: '#001A70',
                              }}
                              width={24}
                              height={24}
                            />
                          </BlueCircleWrapper>
                        }
                        heading={eventDetails.whenDescription ?? ''}
                        description="Public event"
                      />
                    </NavList.Item>
                    <NavList.Item chevron onClick={navToMarkerSummary}>
                      <IconWithTextLockup
                        emphasis="heading"
                        image={
                          <BlueCircleWrapper>
                            <ReactSvg
                              toDisplay={location()}
                              options={{
                                fillColor: '#001A70',
                              }}
                              width={24}
                              height={24}
                            />
                          </BlueCircleWrapper>
                        }
                        heading={eventDetails.locationName}
                        description="View on map"
                      />
                    </NavList.Item>
                  </NavList>
                </Box>
              )}
              {buttonHref && buttonLabel && (
                <DeckHandButton.Group wide>
                  <DeckHandButton
                    onClick={(e) => handleCustomButtonHref(buttonHref, e)}
                  >
                    {buttonLabel}
                  </DeckHandButton>
                  <DeckHandButton
                    outlined
                    onClick={() => {
                      history.push('/maps/share', {
                        sharePath,
                        shareText,
                        title,
                        description,
                        markerType,
                        id,
                      });
                    }}
                  >
                    Share
                  </DeckHandButton>
                </DeckHandButton.Group>
              )}
              {!buttonHref && !buttonLabel && shareText && (
                <CommentAndShare
                  sharePath={sharePath}
                  shareText={shareText}
                  type={markerType}
                  title={title}
                  description={description}
                  id={id}
                  readCommentsAction={readCommentsAction}
                  addCommentsAction={addCommentsAction}
                  allowComments={allowComments}
                  numberOfComments={numberOfComments}
                />
              )}
            </Card.Body>
          </Card>
        )}

        {(description ||
          customDescription ||
          image ||
          (urls && urls.length > 0)) && (
          <Card>
            {/* Marker description or custom description */}
            {(description || customDescription) && (
              <Card.Heading>{descriptionHeaderText}</Card.Heading>
            )}
            <Card.Body>
              {description && (
                <Text
                  className={cx('marker-container__description')}
                  type="light"
                  size="0"
                >
                  {description}
                </Text>
              )}
              {customDescription && (
                <CustomDescription
                  className={cx('marker-container__description')}
                  description={customDescription}
                />
              )}

              {/* Marker main image */}
              {image && (
                <DisplayImage
                  className={cx('marker-container__image')}
                  imageId={image}
                  format="jpg"
                  style={{ display: 'block' }}
                  entryEffect="dropDown1"
                />
              )}

              {/* Extra information buttons and links */}
              {urls && (
                <div className={cx('marker-container__info-buttons')}>
                  {urls &&
                    urls.map((urlItem, index) => (
                      <DeckHandButton
                        className={cx('marker-container__info-button')}
                        key={`info-button-url-${index}`}
                        link
                        size="medium"
                        round
                        href={urlItem.value.url}
                        onClick={(e) => handleLinkClick(urlItem.value.url, e)}
                        icon={externalLink()}
                      >
                        {`External link (${index + 1})`}
                      </DeckHandButton>
                    ))}
                </div>
              )}

              {/* Added or modified date & user */}
              {addedOrEditedByLabel && addedOrEditedByLabel.length > 0 && (
                <Text
                  className={cx('marker-container__modified-date')}
                  type="neutral"
                  size="-2"
                >
                  {addedOrEditedByLabel}
                </Text>
              )}

              {/* Marker edit button */}
              {allowEditing && (
                <Button
                  className={cx('marker-container__edit-button')}
                  icon={pencil()}
                  round
                  action={editRoute}
                  size="medium"
                >
                  Edit
                </Button>
              )}
            </Card.Body>
          </Card>
        )}

        {informationButtons && informationButtons.length > 0 && (
          <Card>
            <Card.Heading>More information</Card.Heading>
            <Card.Body>
              <NavList edge grouped>
                {informationButtons.map((informationButton) => (
                  <InformationButtonListItem
                    label={informationButton.label}
                    url={informationButton.url}
                    key={informationButton.label}
                  />
                ))}
              </NavList>
            </Card.Body>
          </Card>
        )}

        <Card>
          <Card.Body>
            <NavList edge grouped>
              <RegulationLocationCard
                blueVersion
                onClick={() => {
                  transitionToNextRoute(ROUTE_REGULATIONS, {
                    latitude: formattedLatLon?.lat,
                    longitude: formattedLatLon?.lon,
                  });
                }}
                showLatLon={false}
                latitude={formattedLatLon?.lat}
                longitude={formattedLatLon?.lon}
              />
              <NavList.Item chevron onClick={readCommentsAction}>
                <IconWithTextLockup
                  image={
                    <BlueCircleWrapper>
                      <ReactSvg
                        toDisplay={messages()}
                        options={{
                          fillColor: theme.colors.secondary,
                        }}
                        width={24}
                        height={24}
                      />
                    </BlueCircleWrapper>
                  }
                  heading="Community comments"
                />
              </NavList.Item>
            </NavList>
          </Card.Body>
        </Card>

        {/* Marker checkboxes */}
        {checkboxes && checkboxes.length > 0 && (
          <Card p="3">
            {checkboxes.map((checkboxItem) => (
              <Block
                display="flex"
                alignItems="center"
                justifyContent="flex-start"
                key={checkboxItem.label}
              >
                <CheckboxInput
                  readOnly
                  name={checkboxItem.label}
                  checked={checkboxItem.isActive}
                  crossed={!checkboxItem.isActive}
                />
                <Paragraph fontSize="14px" color="#484848" ml={2}>
                  {checkboxItem.label}
                </Paragraph>
              </Block>
            ))}
          </Card>
        )}
      </LayoutBody>
    </div>
  );
};

MarkerContainerView.propTypes = {
  editRoute: PropTypes.func,
  flaggedForReview: PropTypes.bool,
  allowEditing: PropTypes.bool,
  currentMember: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),

  formattedLatLon: PropTypes.shape({
    lat: PropTypes.number,
    lon: PropTypes.number,
  }),
  markerIcon: PropTypes.shape({
    baseShape: PropTypes.func,
    iconColor: PropTypes.string,
    markerColor: PropTypes.string,
  }),
  markerIconDiscColour: PropTypes.string,

  checkboxes: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      isActive: PropTypes.bool,
    }),
  ),
  title: PropTypes.string,
  descriptionHeaderText: PropTypes.string,
  description: PropTypes.string,
  customDescription: PropTypes.arrayOf(PropTypes.shape({})),
  /* To display the authority banner for the source */
  banner: PropTypes.shape({
    source: PropTypes.string,
    official: PropTypes.bool,
  }),
  image: PropTypes.string,
  informationButtons: PropTypes.arrayOf(
    PropTypes.shape({
      /** The display label for the link item */
      label: PropTypes.string.isRequired,

      /** The url for the link item */
      url: PropTypes.string,

      /** The type of link item to display */
      type: PropTypes.string,

      /** The icon to display for the link item */
      icon: PropTypes.string,
    }),
  ),
  eventDetails: PropTypes.shape({
    /** The display label for the link item */
    organizer: PropTypes.string.isRequired,

    /** The url for the link item */
    locationName: PropTypes.string,

    /** The type of link item to display */
    whenDescription: PropTypes.string,

    /** The icon to display for the link item */
    icon: PropTypes.string,
  }),

  addedOrEditedByLabel: PropTypes.string,

  // sharing
  sharePath: PropTypes.string.isRequired,
  shareText: PropTypes.string.isRequired,
  id: PropTypes.string,
  markerType: PropTypes.string,

  // comments
  allowComments: PropTypes.bool,
  readCommentsAction: PropTypes.func,
  addCommentsAction: PropTypes.func,
  numberOfComments: PropTypes.number,

  metaTagURL: PropTypes.string,

  className: PropTypes.string,
  headerImage: PropTypes.string,
};

MarkerContainerView.defaultProps = {
  editRoute: undefined,
  flaggedForReview: false,
  allowEditing: false,
  currentMember: undefined,

  formattedLatLon: null,
  markerIcon: null,
  markerIconDiscColour: null,

  checkboxes: null,
  title: null,
  descriptionHeaderText: null,
  description: null,
  customDescription: null,
  banner: null,
  image: null,
  informationButtons: null,
  addedOrEditedByLabel: null,

  // sharing
  id: null,
  markerType: null,

  // comments
  allowComments: false,
  readCommentsAction: undefined,
  addCommentsAction: undefined,

  className: null,
  numberOfComments: null,
  headerImage: null,
  metaTagURL: null,
  eventDetails: null,
};

export default MarkerContainerView;
